import {
  type CSSProperties,
  type ReactElement,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import EmptyList from 'components/EmptyList/EmptyList';
import { Map, Overlay } from 'pigeon-maps';

import {
  ISSUE_MAP_TILE_LANG_I18N,
  ISSUE_MAP_TILE_LANGS,
  type IssueMapTileLang,
  issueMapTileProvider,
  resolveIssueMapTileLang,
} from './issueMapTiles';
import { mockPulseNow } from './marketingOpsSelectors';
import {
  formatIssueAgeParts,
  type IssueMapMarker,
  type RunIssueCard,
} from './runAnalysisSelectors';

import styles from './scss/TargetedMarketing.module.scss';

const MIN_ZOOM = 2;
const MAX_ZOOM = 12;
const INITIAL_ZOOM = 4;
const INITIAL_CENTER: [number, number] = [47.5, 6];
const MAP_HEIGHT = 360;
const MAP_HEIGHT_SCALES = [1, 2, 3] as const;

type MapHeightScale = (typeof MAP_HEIGHT_SCALES)[number];

function nextMapHeightScale(scale: MapHeightScale): MapHeightScale {
  const index = MAP_HEIGHT_SCALES.indexOf(scale);
  return MAP_HEIGHT_SCALES[(index + 1) % MAP_HEIGHT_SCALES.length];
}

interface IWatchTowerIssueMap {
  markers: IssueMapMarker[],
  selectedCountry: string,
  onSelectCountry: (country: string) => void,
  onOpenIssue: (card: RunIssueCard) => void,
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function markerSize(count: number): number {
  return Math.min(36, 16 + count * 3);
}

function markerClass(severity: string, selected: boolean): string {
  let tone = styles.issueMapMarkerLow;
  if (severity === 'high' || severity === 'critical') {
    tone = styles.issueMapMarkerSevere;
  } else if (severity === 'medium') {
    tone = styles.issueMapMarkerMedium;
  }
  if (selected) {
    return `${tone} ${styles.issueMapMarkerSelected}`;
  }
  return tone;
}

function badgeClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return `${styles.statusPill} ${styles.issueMapBadgeSevere}`;
  }
  if (severity === 'medium') {
    return `${styles.statusPill} ${styles.issueMapBadgeMedium}`;
  }
  return `${styles.statusPill} ${styles.issueMapBadgeLow}`;
}

function langButtonClass(selected: boolean): string {
  if (selected) {
    return `${styles.issueMapControl} ${styles.issueMapControlActive}`;
  }
  return styles.issueMapControl;
}

function WatchTowerIssueMap({
  markers,
  selectedCountry,
  onSelectCountry,
  onOpenIssue,
}: IWatchTowerIssueMap): ReactElement {
  const { t, i18n } = useTranslation();
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [center, setCenter] = useState<[number, number]>(INITIAL_CENTER);
  const [tileLang, setTileLang] = useState<IssueMapTileLang>(
    () => resolveIssueMapTileLang(i18n.language),
  );
  const [query, setQuery] = useState('');
  const [heightScale, setHeightScale] = useState<MapHeightScale>(1);
  const now = mockPulseNow();
  const mapHeight = MAP_HEIGHT * heightScale;
  const tileProvider = useMemo(() => issueMapTileProvider(tileLang), [tileLang]);

  useEffect(() => {
    setTileLang(resolveIssueMapTileLang(i18n.language));
  }, [i18n.language]);

  const sidebarCards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return markers
      .filter((marker) => !selectedCountry || marker.country === selectedCountry)
      .flatMap((marker) => marker.cards)
      .filter((card) => {
        if (!needle) {
          return true;
        }
        const hay = [
          card.city,
          card.country,
          card.companyName,
          card.finding.severity,
          card.finding.title,
          card.agentLabel,
        ].join(' ').toLowerCase();
        return hay.includes(needle);
      });
  }, [markers, query, selectedCountry]);

  const onReset = () => {
    setZoom(INITIAL_ZOOM);
    setCenter(INITIAL_CENTER);
  };

  const ageLabel = (iso: string): string => {
    const parts = formatIssueAgeParts(iso, now);
    if (parts.unit === 'now') {
      return t('targetedMarketing.justNow');
    }
    if (parts.unit === 'minutes') {
      return t('targetedMarketing.minutesAgo', { count: parts.count });
    }
    if (parts.unit === 'hours') {
      return t('targetedMarketing.hoursAgo', { count: parts.count });
    }
    return t('targetedMarketing.daysAgo', { count: parts.count });
  };

  return (
    <div
      className={styles.issueMapLayout}
      style={{ '--issue-map-height': `${mapHeight}px` } as CSSProperties}
    >
      <div
        className={styles.issueMapStage}
        role="region"
        aria-label={t('targetedMarketing.issueMapTitle')}
      >
        <div
          className={styles.issueMapPigeon}
          data-testid="issue-map-surface"
          data-tile-lang={tileLang}
          data-map-height={mapHeight}
        >
          <Map
            key={tileLang}
            height={mapHeight}
            center={center}
            zoom={zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            limitBounds="edge"
            provider={tileProvider}
            boxClassname={styles.issueMapTiles}
            onBoundsChanged={({ center: nextCenter, zoom: nextZoom }) => {
              setCenter([nextCenter[0], nextCenter[1]]);
              setZoom(nextZoom);
            }}
          >
            {markers.map((marker) => {
              const size = markerSize(marker.count);
              const selected = selectedCountry === marker.country;
              return (
                <Overlay
                  key={marker.key}
                  anchor={[marker.lat, marker.lng]}
                  offset={[size / 2, size / 2]}
                  className="pigeon-drag-block"
                >
                  <button
                    type="button"
                    className={markerClass(marker.maxSeverity, selected)}
                    style={{ width: size, height: size }}
                    aria-label={t('targetedMarketing.issueMapMarker', {
                      city: marker.city || t('targetedMarketing.none'),
                      country: marker.country,
                      count: marker.count,
                    })}
                    onClick={() => onSelectCountry(marker.country)}
                  >
                    {marker.count}
                  </button>
                </Overlay>
              );
            })}
          </Map>
        </div>
        <div
          className={styles.issueMapLang}
          role="group"
          aria-label={t('targetedMarketing.issueMapLang')}
        >
          {ISSUE_MAP_TILE_LANGS.map((lang) => {
            const selected = tileLang === lang;
            return (
              <button
                key={lang}
                type="button"
                className={langButtonClass(selected)}
                aria-pressed={selected}
                aria-label={t(ISSUE_MAP_TILE_LANG_I18N[lang])}
                onClick={() => setTileLang(lang)}
              >
                {lang.toUpperCase()}
              </button>
            );
          })}
        </div>
        <div className={styles.issueMapControls}>
          <button
            type="button"
            className={styles.issueMapControl}
            aria-label={t('targetedMarketing.issueMapZoomIn')}
            onClick={() => setZoom(clamp(zoom + 1, MIN_ZOOM, MAX_ZOOM))}
          >
            +
          </button>
          <button
            type="button"
            className={styles.issueMapControl}
            aria-label={t('targetedMarketing.issueMapZoomOut')}
            onClick={() => setZoom(clamp(zoom - 1, MIN_ZOOM, MAX_ZOOM))}
          >
            −
          </button>
          <button
            type="button"
            className={styles.issueMapControl}
            aria-label={t('targetedMarketing.issueMapReset')}
            onClick={onReset}
          >
            {t('targetedMarketing.issueMapResetShort')}
          </button>
          <button
            type="button"
            className={heightScale > 1
              ? `${styles.issueMapControl} ${styles.issueMapControlActive}`
              : styles.issueMapControl}
            aria-label={t('targetedMarketing.issueMapHeight', { scale: heightScale })}
            onClick={() => setHeightScale((scale) => nextMapHeightScale(scale))}
          >
            {t('targetedMarketing.issueMapHeightShort', { scale: heightScale })}
          </button>
        </div>
        <ul className={styles.issueMapLegend}>
          <li>
            <span className={`${styles.issueMapLegendSwatch} ${styles.issueMapLegendSevere}`} />
            {t('targetedMarketing.issueMapLegendSevere')}
          </li>
          <li>
            <span className={`${styles.issueMapLegendSwatch} ${styles.issueMapLegendMedium}`} />
            {t('targetedMarketing.issueMapLegendMedium')}
          </li>
          <li>
            <span className={`${styles.issueMapLegendSwatch} ${styles.issueMapLegendLow}`} />
            {t('targetedMarketing.issueMapLegendLow')}
          </li>
        </ul>
        {markers.length === 0 ? (
          <div className={styles.issueMapEmpty}>
            <EmptyList text={t('targetedMarketing.issueMapEmpty')} />
          </div>
        ) : null}
      </div>
      <aside className={styles.issueMapSidebar}>
        <div className={styles.issueMapSidebarHead}>
          <h4 className={styles.issueMapSidebarTitle}>
            {t('targetedMarketing.issueMapLocations')}
          </h4>
          <p className={styles.issueMapSidebarCount}>{sidebarCards.length}</p>
        </div>
        <label className={styles.issueMapFilter} htmlFor="wt-issue-map-filter">
          <span className={styles.srOnly}>{t('targetedMarketing.issueMapFilter')}</span>
          <input
            id="wt-issue-map-filter"
            className={styles.filterNative}
            value={query}
            placeholder={t('targetedMarketing.issueMapFilterPlaceholder')}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {sidebarCards.length === 0 ? (
          <EmptyList text={t('targetedMarketing.issueMapEmpty')} />
        ) : (
          <ul className={styles.issueMapEventList}>
            {sidebarCards.map((card) => (
              <li key={card.finding.publicId}>
                <button
                  type="button"
                  className={styles.issueMapEvent}
                  onClick={() => onOpenIssue(card)}
                >
                  <span className={badgeClass(card.finding.severity)}>
                    {card.finding.severity}
                  </span>
                  <span className={styles.issueMapEventMeta}>
                    {ageLabel(card.finding.observedAt)}
                  </span>
                  <span className={styles.issueMapEventTitle}>{card.finding.title}</span>
                  <span className={styles.issueMapEventCompany}>{card.companyName}</span>
                  <span className={styles.issueMapEventPlace}>
                    {t('targetedMarketing.issueMapLocation', {
                      city: card.city || t('targetedMarketing.none'),
                      country: card.country || t('targetedMarketing.none'),
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}

export default WatchTowerIssueMap;
